import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

const LINE_HOLD = 2600;
const FADE_OUT = 600;
const TAGLINE_DELAY = 800;
const GAP = 400;

const LINES: { key: string; accent?: boolean }[] = [
  { key: "line1Parts" },
  { key: "line2Parts" },
  { key: "line4Parts", accent: true },
];

export default function RelarkPainPoint() {
  const { t, i18n } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn();
  const [lineIdx, setLineIdx] = useState(-1);
  const [fading, setFading] = useState(false);
  const [done, setDone] = useState(false);
  const [showTagline, setShowTagline] = useState(false);
  const startedRef = useRef(false);
  const [resourcesReady, setResourcesReady] = useState(() =>
    i18n.hasResourceBundle(i18n.language, "ui"),
  );

  useEffect(() => {
    if (resourcesReady) return;
    const check = () => {
      if (i18n.hasResourceBundle(i18n.language, "ui")) setResourcesReady(true);
    };
    check();
    i18n.on("loaded", check);
    i18n.on("languageChanged", check);
    return () => {
      i18n.off("loaded", check);
      i18n.off("languageChanged", check);
    };
  }, [i18n, resourcesReady]);

  useEffect(() => {
    if (!isVisible || !resourcesReady || startedRef.current) return;
    startedRef.current = true;

    const timers: ReturnType<typeof setTimeout>[] = [];
    let cursor = 100;

    LINES.forEach((_, li) => {
      const showAt = cursor;
      timers.push(
        setTimeout(() => {
          setLineIdx(li);
          setFading(false);
        }, showAt),
      );
      cursor += LINE_HOLD;

      if (li < LINES.length - 1) {
        const fc = cursor;
        timers.push(setTimeout(() => setFading(true), fc));
        cursor += FADE_OUT + GAP;
      } else {
        const dc = cursor + 200;
        timers.push(setTimeout(() => setDone(true), dc));
        timers.push(setTimeout(() => setShowTagline(true), dc + TAGLINE_DELAY));
      }
    });

    return () => timers.forEach(clearTimeout);
  }, [isVisible, resourcesReady, t]);

  return (
    <section className="py-16 sm:py-24">
      <div ref={ref} className="relative mx-auto max-w-2xl px-5 min-h-[260px] sm:min-h-[220px] lg:min-h-[200px]">
        {LINES.map((line, li) => {
          const parts: string[] = t(`relark.painPoint.${line.key}`, { returnObjects: true }) as string[];
          if (!Array.isArray(parts)) return null;

          const isActive = lineIdx === li;
          const isPast = lineIdx > li;
          const isFuture = lineIdx < li;
          const isLast = li === LINES.length - 1;

          const containerCls = [
            isLast && done ? "relative" : "absolute inset-0 flex items-center justify-center",
            "transition-opacity",
            (isPast || isFuture || (isActive && fading)) ? "opacity-0" : "opacity-100",
          ].join(" ");

          return (
            <div
              key={line.key}
              className={containerCls}
              style={{ transitionDuration: `${FADE_OUT}ms` }}
            >
              <p className={`text-center leading-relaxed ${
                isLast ? "text-lg sm:text-xl font-medium" : "text-lg sm:text-xl"
              } ${isLast && done ? "text-foreground" : "text-muted-foreground"} transition-colors`}
                style={{ transitionDuration: "1.4s" }}
              >
                <span
                  className="transition-all inline-block"
                  style={{
                    opacity: isActive && !fading ? 1 : 0,
                    filter: isActive && !fading ? "blur(0px)" : "blur(6px)",
                    transitionDuration: "700ms",
                  }}
                >
                  {parts.join(" ")}
                </span>
              </p>
            </div>
          );
        })}

        <p
          className="text-center text-2xl sm:text-3xl lg:text-4xl font-bold mt-5 transition-all"
          style={{
            opacity: showTagline ? 1 : 0,
            filter: showTagline ? "blur(0px)" : "blur(6px)",
            transitionDuration: "700ms",
          }}
        >
          <span className="text-primary">Relark</span>
          {" "}
          {t("relark.painPoint.taglineSuffix").split(/(beslutningskompas|decision compass|brújula de decisión|boussole de décision|beslutningskompass|bússola de decisão|beslutskompass|Entscheidungs-Kompass)/i).map((part, i) =>
            /^(beslutningskompas|decision compass|brújula de decisión|boussole de décision|beslutningskompass|bússola de decisão|beslutskompass|Entscheidungs-Kompass)$/i.test(part)
              ? <span key={i} className="text-primary">{part}</span>
              : <span key={i} className="text-foreground">{part}</span>
          )}
        </p>
        <p
          className="text-center text-sm sm:text-base text-muted-foreground mt-4 transition-all"
          style={{
            opacity: showTagline ? 1 : 0,
            filter: showTagline ? "blur(0px)" : "blur(6px)",
            transitionDuration: "700ms",
            transitionDelay: showTagline ? "200ms" : "0ms",
          }}
        >
          {t("relark.painPoint.taglineSupport")}
        </p>
      </div>
    </section>
  );
}